import React, { useMemo } from 'react';
import { colours } from './colours';
const HexMap = ({
    activeConstituency,
    selectedOutlet,
    searchMode,
    outlets,
    onHexClick,
    onHexHover,
    mapMetric = 'total',
    boundaryType = 'constituency',
    dynamicScale,
    hexData,
    statsData
}) => {
    const idKey = boundaryType === 'constituency' ? 'PCON24CD' : 'LAD24CD';
    const coverageKey = boundaryType === 'constituency' ? 'COVERAGE_CONSTITUENCY' : 'COVERAGE_LAD';
    const size = 10; // Base size of the hexagon
    const width = Math.sqrt(3) * size;
    const height = 2 * size;

    // Calculate outlet counts for colors
    const outletsByConstituency = useMemo(() => {
        const stats = {};
        if (!hexData || !hexData.hexes || !statsData) return stats;

        Object.keys(hexData.hexes).forEach(id => {
            const outletsInConstituency = outlets.filter(o => o[coverageKey]?.includes(id));
            const stat = statsData.find(s => s.id === id);

            const total = outletsInConstituency.length;
            const per100k = stat ? (total / (stat.pop / 100000)) : 0;

            stats[id] = { total, per100k };
        });
        return stats;
    }, [outlets, hexData, statsData, coverageKey]);

    // Normalize grid
    const { bounds, hexes } = useMemo(() => {
        let minQ = Infinity, maxQ = -Infinity, minR = Infinity, maxR = -Infinity;

        Object.values(hexData.hexes).forEach(hex => {
            minQ = Math.min(minQ, hex.q);
            maxQ = Math.max(maxQ, hex.q);
            minR = Math.min(minR, hex.r);
            maxR = Math.max(maxR, hex.r);
        });

        const items = Object.entries(hexData.hexes).map(([id, data]) => {
            // Logic for odd-r layout
            // We invert r because the data uses negative Y-axis convention (r increases upwards)
            const normalizedQ = data.q - minQ;

            const x = width * (normalizedQ + 0.5 * (Math.abs(data.r) % 2));
            const y = (height * 3 / 4) * (maxR - data.r); // Invert vertical for SVG (0,0 is top-left)

            return { id, ...data, x, y };
        });

        return {
            bounds: {
                width: (maxQ - minQ + 2) * width,
                height: (maxR - minR + 2) * (height * 3 / 4)
            },
            hexes: items
        };
    }, [hexData, width, height]);

    const getHexPoints = (cx, cy, r) => {
        const points = [];
        for (let i = 0; i < 6; i++) {
            const angle_deg = 60 * i - 30; // Pointy top
            const angle_rad = (Math.PI / 180) * angle_deg;
            points.push(`${cx + r * Math.cos(angle_rad)},${cy + r * Math.sin(angle_rad)}`);
        }
        return points.join(' ');
    };

    const getColor = (id) => {
        if (searchMode === 'outlet') {
            const isCovered = selectedOutlet?.[coverageKey]?.includes(id);
            return isCovered ? colours.BLUE : colours.WHITE;
        }

        const stat = outletsByConstituency[id];
        const val = mapMetric === 'per100k' ? (stat ? stat.per100k : 0) : (stat ? stat.total : 0);

        // Dynamic scale thresholds
        const scale = mapMetric === 'per100k' ? (dynamicScale || colours.SCALE_100K) : colours.SCALE;

        const bin = scale.find(s => val >= s.min && val <= s.max);
        return bin ? bin.color : colours.WHITE;
    };

    return (
        <div className="hex-map-container" onMouseLeave={() => onHexHover(null)}>
            <svg
                viewBox={`-30 -30 ${bounds.width + 60} ${bounds.height + 60}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ width: '95%', height: '95%' }}
            >
                <defs>
                    <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                        <feOffset dx="0" dy="2" result="offsetblur" />
                        <feComponentTransfer>
                            <feFuncA type="linear" slope="0.6" />
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>
                {/* Render normal hexes first */}
                {hexes.filter(h => activeConstituency?.[idKey] !== h.id).map((hex) => {
                    const isHighlighted = searchMode === 'outlet' && selectedOutlet?.[coverageKey]?.includes(hex.id);
                    return (
                        <polygon
                            key={hex.id}
                            points={getHexPoints(hex.x, hex.y, size)}
                            fill={getColor(hex.id)}
                            stroke={isHighlighted ? "#265089" : "#eee"}
                            strokeWidth={isHighlighted ? 1.5 : 0.5}
                            className="hex-cell"
                            onClick={() => onHexClick(hex)}
                            onMouseEnter={(e) => onHexHover(hex, e)}
                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                        />
                    );
                })}
                {/* Render active hex last to be on top */}
                {hexes.filter(h => activeConstituency?.[idKey] === h.id).map((hex) => (
                    <polygon
                        key={hex.id}
                        points={getHexPoints(hex.x, hex.y, size)}
                        fill={getColor(hex.id)}
                        stroke="#fff"
                        strokeWidth={4}
                        filter="url(#shadow)"
                        className="hex-cell active"
                        onClick={() => onHexClick(hex)}
                        onMouseEnter={(e) => onHexHover(hex, e)}
                        style={{
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            transform: 'scale(1.15)',
                            transformOrigin: `${hex.x}px ${hex.y}px`,
                            zIndex: 10
                        }}
                    />
                ))}
            </svg>
        </div>
    );
};

export default React.memo(HexMap);
